<template>
  <div class="p-5 flex flex-col">
    <p class="font-semibold text-3xl">
      {{ this.$route.meta.title }}
    </p>
    <vs-row
      vs-w="12"
      class="my-5 filter-container flex justify-between"
    >
      <vs-col vs-w="1">
        <p class="font-semibold">
          Filter
        </p>
      </vs-col>
      <vs-col vs-w="3" />
      <vs-col
        vs-w="8"
        class="flex justify-end"
      >
        <div class="flex flex-row gap-x-4 items-stretch">
          <div class="flex items-center w-full">
            <select-with-search
              v-model="filters.is_active_filter"
              hide-search
              placeholder="Select is active"
              :options="stateOptions"
              style="min-width: 200px;"
            />
          </div>
          <div class="flex items-center">
            <vs-button
              type="border"
              color="primary"
              class="self-center font-semibold"
              style="min-width: 100px"
              @click="resetFilters"
            >
              Reset
            </vs-button>
          </div>
        </div>
      </vs-col>
    </vs-row>
    <div class="p-5 flex flex-col mt-5 bg-white rounded-md">
      <table-top
        is-search-has-border
        :page-size-start="false"
        class="mt-5"
        @fetch="fetchVesselManagement"
        ref="tabletop"
      >
        <template #start>
          <div class="flex items-center justify-center">
            <vs-button
              class="button shadow-secondary"
              color="secondary"
              @click="isDownload = true"
              :disabled="isDownload"
            >
              Export Excel
            </vs-button>
          </div>
        </template>
      </table-top>
      <data-table
        class="my-5"
        :loading="isBusy"
        :table-header="tableHeaders"
        :table-data="vesselManagementData"
        :sort-by="filters.sort_by"
        :sort-dir="filters.sort_dir"
        @sort="sort"
      >
        <template #no="{ index }">
          <div class="flex flex-row w-min">
            {{ (currentPage - 1) * pageSize + index + 1 }}
          </div>
        </template>
        <template #action="{ row }">
          <div class="flex flex-row gap-x-3">
            <template v-if="row.isEditing">
              <div
                :class="`flex items-center justify-center icon-transparent-bg complete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click=" isProcessing ? null : saveEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-check-circle.svg')"
                  alt="save"
                >
              </div>
              <div
                :class="`flex items-center justify-center icon-transparent-bg delete ${isProcessing && 'cursor-not-allowed animate-bounce opacity-50'}`"
                @click=" isProcessing ? null : cancelEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-close-circle.svg')"
                  alt="cancel"
                >
              </div>
            </template>
            <template v-else>
              <div
                v-if="isUserCanAccessMenu(permission.name, 'edit')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="startEdit(row)"
              >
                <img
                  :src="require('@/assets/shipo-icon-edit.svg')"
                  alt="edit"
                >
              </div>
              <div
                v-if="isUserCanAccessMenu(permission.name, 'delete')"
                class="flex items-center justify-center icon-transparent-bg"
                @click="popUp = { isShown: true, tempId: row.id, tempName: row.vessel_name }"
              >
                <img
                  :src="require('@/assets/shipo-icon-delete.svg')"
                  alt="delete"
                >
              </div>
            </template>
          </div>
        </template>
        <template #vessel_name="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <div>
                <vs-input
                  v-model="row.editForm.vessel_name.value"
                  :class="{ 'is-not-valid': !row.editForm.vessel_name.isValid }"
                  placeholder="Vessel Name"
                  :disabled="isProcessing"
                />
              </div>
              <span
                v-if="!row.editForm.vessel_name.value"
                class="text-danger text-sm absolute-bottom-5 left-0"
              >*Required</span>
            </div>
          </template>
          <template v-else>
            {{ row.vessel_name || '-' }}
          </template>
        </template>
        <template #marine_traffic_id="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-input
                v-model="row.editForm.marine_traffic_id.value"
                :class="{ 
                  'is-not-valid': !row.editForm.marine_traffic_id.isValid || 
                    (row.editForm.marine_traffic_id.value && !isValidMarineTrafficId(row.editForm.marine_traffic_id.value))
                }"
                placeholder="Marine Traffic ID"
                :disabled="isProcessing"
                @input="validateMarineTrafficIdInput(row)"
              >
                <span
                  v-if="!row.editForm.marine_traffic_id.value"
                  class="text-danger text-sm absolute-bottom-5 left-0"
                >*Required</span>
              </vs-input>
            </div>
          </template>
          <template v-else>
            {{ row.marine_traffic_id || '-' }}
          </template>
        </template>
        <template #is_active="{ row }">
          <template v-if="row.isEditing">
            <div class="relative">
              <vs-switch
                v-model="row.editForm.is_active.value"
                color="secondary"
                :disabled="!isUserCanAccessMenu(permission.name, 'is_active') || !row.isEditing || isProcessing"
              />
            </div>
          </template>
          <template v-else>
            <div
              :class="{
                'bg-green-200 text-green-700 border-green-400': row.is_active === 'true',
                'bg-red-200 text-red-700 border-red-400': row.is_active === 'false'
              }"
              :disabled="!isUserCanAccessMenu(permission.name, 'is_active')"
              class="button-l-plan w-20 items-center"
            >
              <span>{{ row.is_active !== null ? stateOptions.find((state) => state.value === row.is_active.toString()).text : '-' }}</span>
            </div>
          </template>
        </template>
        <template #created_date="{ row }">
          {{ row.created_date ? moment(row.created_date).format('DD-MMM-YYYY HH:mm') : '-' }}
        </template>
      </data-table>
      <table-bottom 
        class="mt-7"
        @fetch="fetchVesselManagement()" 
      />
    </div>
    <pop-up
      :active.sync="popUp.isShown"
      :is-warning="true"
      :title="'Delete Vessel Management'"
      :left-button="'Cancel'"
      :right-button="'Yes, Delete'"
      :is-busy="isProcessing"
      @close="popUp = { isShown: false, tempId: null, tempName: '' }"
      @leftButton="popUp = { isShown: false, tempId: null, tempName: '' }"
      @rightButton="deleteLocation(popUp.tempId)"
    >
      <template>
        <p class="text-center mt-1">
          Are you sure want to delete vessel management "{{ popUp.tempName }}"?
        </p>
        <p class="text-center">
          This action cannot be undone
        </p>
      </template>
    </pop-up>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'

import DataTable from '@/components/dataTable'
import PopUp from '@/components/popUp'
import SelectWithSearch from '@/components/selectWithSearch'
import TableBottom from '@/components/tableBottom'
import TableTop from '@/components/tableTop'
import { PAGE_TITLE } from '@/fixtures/Constants'

import downloadUtils from '../../../utils/downloadUtils'


export default {
  components: {
    DataTable,
    TableBottom,
    TableTop,
    PopUp,
    SelectWithSearch
  },
  metaInfo() {
    return {
      title: `${PAGE_TITLE} ${this.$route.meta.title}`
    }
  },
  data() {
    return {
      isDownload: false,
      isBusy: false,
      isProcessing: false,
      filters: {
        sort_by: 'vessel_name',
        sort_dir: 'asc',
        is_active_filter: ''
      },
      tableHeaders: [
        { key: 'no', label: 'No', sortable: false, width: '20px' },
        { key: 'vessel_name', label: 'Vessel Name', sortable: true, width: '200px' },
        { key: 'marine_traffic_id', label: 'Marine Traffic ID', sortable: true, width: '150px' },
        { key: 'is_active', label: 'Is Active?', sortable: true, width: '100px' },
        { key: 'created_date', label: 'Created Date', sortable: true, width: '200px' }
      ],
      vesselManagementData: [],
      isAdding: false,
      isEditing: false,
      editForm: {
      },
      popUp: {
        isShown: false,
        tempId: null,
        tempName: '',
        isDuplicateDate: false,
        duplicateData: null
      },
      stateOptions: [
        { value: 'true', text: 'Active' },
        { value: 'false', text: 'Inactive' }
      ],
      allVesselManagementData: []
    }
  },
  computed: {
    ...mapGetters({
      currentPage: 'Table/currentPage',
      pageSize: 'Table/pageSize',
      vesselManagement: 'VesselManagement/vesselManagement',
      vesselManagementFiles: 'VesselManagement/vesselManagementFiles'
    }),
    permission() {
      return { page: this.$route.meta.permission.page, name: this.$route.meta.permission.name }
    },
    isActionColumnVisible() {
      return this.isUserCanAccessMenu(this.permission.name, 'edit') || this.isUserCanAccessMenu(this.permission.name, 'delete')
    }
  },
  watch: {
    vesselManagement: {
      handler(val) {
        if (val) {
          this.vesselManagementData = val.map(item => ({
            ...item,
            isEditing: false,
            editForm: null
          }))
        }
      }
    },
    isDownload: {
      handler(val) {
        if (!!val) {
          this.downloadVesselManagement()
        }
      }
    },
    vesselManagementFiles: {
      handler(val) {
        if (!this.isEmpty(val)) {
          downloadUtils(val, `Vessel List.xlsx`)
          // Reset the export file state
          this.$store.dispatch('VesselManagement/resetVesselManagementFiles');
        }
      }
    },
    isActionColumnVisible: {
      immediate: true,
      handler(val) {
        const index = this.tableHeaders.findIndex(header => header.key === 'action');
        if (val && index === -1) {
          this.tableHeaders.splice(1, 0, { key: 'action', label: 'Action', sortable: false, width: '80px' });
        } else if (!val && index !== -1) {
          this.tableHeaders.splice(index, 1);
        }
      }
    },
    filters: {
      deep: true,
      handler() {
        this.fetchVesselManagement()
      }
    }
  },
  mounted() {
    this.fetchVesselManagement()
  },
  methods: {
    async fetchVesselManagement() {
      try {
        this.isBusy = true
        const apiFilters = {
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'vessel_name',
          page: this.currentPage || 1,
          limit: this.pageSize || 10
        };
        if (this.filters.is_active_filter) {
          apiFilters.is_active = this.filters.is_active_filter
        }

        await this.$store.dispatch('VesselManagement/fetchVesselManagement', apiFilters)

        this.isAdding = false
        this.isBusy = false
      } catch (e) {
        this.isBusy = false
        this.showFailedNotification(this, 'Failed to fetch Vessel Management list, please try again')
      }
    },

    async fetchAllVesselManagementData() {
      try {
        const apiFilters = {
          sort_dir: 'asc',
          sort_by: 'vessel_name',
          page: 1,
          limit: 1000
        };

        const data = await this.$store.dispatch('VesselManagement/fetchVesselManagement', apiFilters)
        this.allVesselManagementData = data.data || []
      } catch (e) {
        this.allVesselManagementData = []
      }
    },

    sort(sortBy, sortDir) {
      this.filters.sort_by = sortBy
      this.filters.sort_dir = sortDir
      this.fetchVesselManagement()
    },
    resetFilters() {
      this.filters = {
        sort_by: 'is_active',
        sort_dir: 'asc',
        is_active_filter: ''
      }
      this.$store.dispatch('Table/resetConfig')
      this.$refs.tabletop && this.$refs.tabletop.$emit('reset-search')
      this.fetchVesselManagement()
    },
    startAdd() {
      if (this.vesselManagementData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first')
        return
      }

      const newRow = {
        id: `new-${Date.now()}`,
        vessel_name: '',
        marine_traffic_id: '',
        is_active: '',
        created_at: '',
        isEditing: true,
        editForm: {
          vessel_name: { value: '', isValid: true },
          marine_traffic_id: { value: '', isValid: true },
          is_active: { value: '', isValid: true }
        }
      };

      this.isAdding = true;
      this.vesselManagementData.unshift(newRow);
    },
    startEdit(row) {
      if (this.vesselManagementData.some(r => r.isEditing)) {
        this.showWarningNotification(this, 'Only one action can be active at a time. Please save your changes first')
        return
      }

      const index = this.vesselManagementData.findIndex(item => item.id === row.id);
      if (index !== -1) {
        this.$set(this.vesselManagementData[index], 'isEditing', true);
        this.$set(this.vesselManagementData[index], 'editForm', {
          vessel_name: { value: row.vessel_name, isValid: true },
          marine_traffic_id: { value: row.marine_traffic_id, isValid: true },
          is_active: { value: row.is_active === 'true' || row.is_active === true }
        });
      }
    },
    startDelete(row) {
      this.popUp = {
        isShown: true,
        tempId: row.id,
        tempName: row.vessel_name
      }
    },
    async saveEdit(row) {
      if (!row.editForm.vessel_name.value) {
        this.showFailedNotification(this, 'Vessel Name is required')
        return
      }
      if (!row.editForm.marine_traffic_id.value) {
        this.showFailedNotification(this, 'Marine Traffic ID is required')
        return
      }

      if (!this.isValidMarineTrafficId(row.editForm.marine_traffic_id.value)) {
        this.showFailedNotification(this, 'Marine Traffic ID must contain only numbers and alphabets')
        return
      }
      this.isProcessing = true
      // Check for duplicate data
      const isDuplicateData = await this.checkDuplicateData(row)
      if (isDuplicateData) {
        this.isProcessing = false
        return
      }

      try {
        if (this.isAdding) {
          await this.$store.dispatch('VesselManagement/postVesselManagement', {
            vessel_name: row.editForm.vessel_name.value,
            marine_traffic_id: row.editForm.marine_traffic_id.value,
            is_active: row.editForm.is_active.value
          })
        } else {
          await this.$store.dispatch('VesselManagement/putVesselManagement', {
            id: row.id,
            form: {
              vessel_name: row.editForm.vessel_name.value,
              marine_traffic_id: row.editForm.marine_traffic_id.value,
              is_active: row.editForm.is_active.value
            }
          })
        }
        this.showSuccessNotification(this, 'Vessel management successfully saved')
        this.cancelAllEdits()
        this.isProcessing = false
        this.fetchVesselManagement()
      } catch (error) {
        this.isProcessing = false
        this.showFailedNotification(this, error?.errors ?? (error?.errors || 'Failed to save changes, please try again'))
      }
    },

    isValidMarineTrafficId(value) {
      const regex = /^[a-zA-Z0-9]+$/
      return regex.test(value)
    },

    validateMarineTrafficIdInput(row) {
      if (row.editForm && row.editForm.marine_traffic_id) {
        row.editForm.marine_traffic_id.isValid = this.isValidMarineTrafficId(row.editForm.marine_traffic_id.value)
      }
    },

    async checkDuplicateData(row) {
      try {
        const params = {
          vessel_name: row.editForm.vessel_name.value,
          marine_traffic_id: row.editForm.marine_traffic_id.value
        }
        
        if (!row.id.toString().startsWith('new-')) {
          params.exclude_id = row.id
        }
        
        const responseVesselName = await this.$store.dispatch('VesselManagement/getVesselCheckExist', {
          id: row.id,
          params: {
            vessel_name: row.editForm.vessel_name.value
          }
        })
        const responseMarineTrafficId = await this.$store.dispatch('VesselManagement/getVesselCheckExist', {
          id: row.id,
          params: {
            marine_traffic_id: row.editForm.marine_traffic_id.value
          }
        })
        if (responseVesselName && responseVesselName.message) {
          this.showFailedNotification(this, responseVesselName.message)
          return true
        }
        
        if (responseMarineTrafficId && responseMarineTrafficId.message) {
          this.showFailedNotification(this, responseMarineTrafficId.message)
          return true
        }
        
        return false
      } catch (error) {
        this.showFailedNotification(this, 'Error checking for duplicate data, please try again')
        return true
      }
    },
    cancelEdit(row) {
      const index = this.vesselManagementData.findIndex(item => item.id === row.id)
      if (index !== -1) {
        if (row.id.toString().startsWith('new-')) {
          this.vesselManagementData.splice(index, 1)
        } else {
          this.$set(this.vesselManagementData[index], 'isEditing', false)
          this.$set(this.vesselManagementData[index], 'editForm', null)
        }
      }
    },
    cancelAllEdits() {
      this.vesselManagementData.forEach((item, index) => {
        if (item.isEditing) {
          if (item.id.toString().startsWith('new-')) {
            this.vesselManagementData.splice(index, 1)
          } else {
            this.$set(this.vesselManagementData[index], 'isEditing', false)
            this.$set(this.vesselManagementData[index], 'editForm', null)
          }
        }
      })
    },
    async deleteLocation(id) {
      try {
        this.isProcessing = true
        await this.$store.dispatch('VesselManagement/deleteVesselManagement', id)
        this.fetchVesselManagement()
        this.showSuccessNotification(this, 'Vessel management deleted successfully')
        this.popUp = { isShown: false, tempId: null, tempName: '', isDuplicateDate: false, duplicateData: null }
        this.isProcessing = false
      } catch (error) {
        this.isProcessing = false
        this.showFailedNotification(this, 'Failed to delete vessel management, please try again')
      }
    },
    async downloadVesselManagement() {
      try {
        this.isBusy = true
        const downloadParams = {
          sort_dir: this.filters.sort_dir || 'asc',
          sort_by: this.filters.sort_by || 'is_active'
        }
        
        if (this.filters.is_active_filter) {
          downloadParams.is_active = this.filters.is_active_filter
        }
        
        await this.$store.dispatch('VesselManagement/downloadVesselManagement', downloadParams, {
          headers: {
            accept: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
          },
          responseType: 'blob'
        });
        this.isDownload = false;
        this.isBusy = false;
      } catch (error) {
        this.isBusy = false;
        this.isDownload = false;
        this.showFailedNotification(this, 'Failed to download Vessel Management, please try again');
      }
    }
  }
}
</script>

<style scoped>
.filter-container {
  @apply w-full p-3 mt-5 flex flex-row items-center bg-white rounded-lg;
}

.icon-transparent-bg {
  @apply cursor-pointer;
  @apply min-w-8;
  @apply h-8;
  @apply bg-primary;
  border-radius: 0.5rem;

  &.complete {
    background-color: #D1F9E3;
  }

  &.delete {
    background-color: rgba(255, 99, 123, 0.16);
  }
}

.button-l-plan {
  @apply flex;
  @apply items-center;
  @apply justify-center;
  padding: 0.15rem 0.25rem;
  border-radius: 5px;
}
</style>
